import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const CommunityValueChart = ({ metrics }) => {
  // Calculate 30-year lifecycle showing DEVEX, CAPEX, and community value
  // Community value only starts when turbines become operational
  const chartData = useMemo(() => {
    const data = [];
    const indexRate = 0.02; // 2% annual indexation for land rent & business rates (from config)
    const lifecycleYears = 30;
    
    // Import the corrected community fund profile (already includes debt repayment & indexation)
    // This profile has 26 values (index 0-25): Year 0 = 0, Years 1-25 = actual values
    const communityFundProfile = metrics.communityFundProfile || [];
    
    // Find first year with operational turbines (when installations begin)
    const firstOperationalYear = metrics.phasingTable.findIndex(row => row.installed > 0);
    
    // Track operational turbines by year (for phasing the community fund)
    // We need to know which turbines became operational in which year to offset the profile correctly
    const turbinesByOperationalYear = [];
    for (let year = 0; year < metrics.phasingTable.length; year++) {
      if (metrics.phasingTable[year].installed > 0) {
        turbinesByOperationalYear.push({
          year,
          count: metrics.phasingTable[year].installed
        });
      }
    }
    
    // Accumulators for cumulative values
    let cumulativeFund = 0;
    let cumulativeLandRent = 0;
    let cumulativeBusinessRates = 0;
    
    for (let year = 0; year < lifecycleYears; year++) {
      // GB Energy DEVEX - from phasing data (Years 1-4 typically)
      const devex = year < metrics.phasingTable.length 
        ? metrics.phasingTable[year].annualDevelopment / 1000000
        : 0;
      
      // Leveraged CAPEX - occurs during installation years
      // CAPEX happens when turbines are installed (around Years 2-6)
      const capex = year < metrics.phasingTable.length
        ? (metrics.phasingTable[year].installed * 5500000) / 1000000
        : 0;
      
      // Calculate annual community fund income using the phased profile
      // For each tranche of turbines, find their years of operation and sum their contributions
      // Community fund income starts the year AFTER installation
      let annualFund = 0;
      for (const tranche of turbinesByOperationalYear) {
        const yearsOperational = year - tranche.year; // How many years since this tranche became operational
        // Profile: [0, year1_value, year2_value, ..., year30_value]
        // Income starts the year after installation: yearsOperational=1 uses profile[1]
        if (yearsOperational >= 1 && yearsOperational < communityFundProfile.length) {
          const profileValue = communityFundProfile[yearsOperational] || 0;
          annualFund += profileValue * tranche.count;
        }
      }
      
      // Calculate land rent and business rates with 3% indexation (they still use the old approach)
      let annualLandRent = 0;
      let annualBusinessRates = 0;
      let operationalTurbines = 0;
      
      // Count operational turbines up to this year
      for (const tranche of turbinesByOperationalYear) {
        if (tranche.year <= year) {
          operationalTurbines += tranche.count;
        }
      }
      
      if (operationalTurbines > 0) {
        // Apply indexation from first operational year
        const yearsOperational = year - firstOperationalYear;
        const indexMultiplier = yearsOperational >= 0 ? Math.pow(1 + indexRate, yearsOperational) : 1;
        
        // Calculate annual income based on operational turbines
        const incomeFraction = operationalTurbines / metrics.turbineCount;
        annualLandRent = metrics.annualLandRent * incomeFraction * indexMultiplier;
        annualBusinessRates = metrics.annualBusinessRates * incomeFraction * indexMultiplier;
      }
      
      // Accumulate
      cumulativeFund += annualFund;
      cumulativeLandRent += annualLandRent;
      cumulativeBusinessRates += annualBusinessRates;
      
      data.push({
        year: `Year ${year + 1}`,
        yearNum: year + 1,
        devex: devex,
        capex: capex,
        communityFund: cumulativeFund / 1000000,
        landRent: cumulativeLandRent / 1000000,
        businessRates: cumulativeBusinessRates / 1000000,
        totalCommunity: (cumulativeFund + cumulativeLandRent + cumulativeBusinessRates) / 1000000,
        isOperational: operationalTurbines > 0
      });
    }
    
    return data;
  }, [metrics]);

  // Find transition year (when community returns begin - year after turbines become operational)
  const firstOperationalYear = chartData.findIndex(d => d.isOperational);
  const transitionYear = firstOperationalYear >= 0 ? firstOperationalYear + 1 : -1;

  // Get Year 25 total for display badge
  const year25Total = chartData.length > 0 ? chartData[chartData.length - 1].totalCommunity : 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const showInvestment = data.devex > 0 || data.capex > 0;
      
      return (
        <div className="bg-white p-4 border-2 rounded-lg shadow-xl" style={{ borderColor: '#48A46A', minWidth: '240px' }}>
          <p className="font-bold mb-3 text-base" style={{ color: '#2C3E50' }}>
            Year {data.yearNum}
          </p>
          
          {/* Investment section */}
          {showInvestment && (
            <div className="mb-3 pb-3 border-b-2" style={{ borderColor: '#e0e0e0' }}>
              <p className="text-xs font-semibold mb-2 text-gray-600">Investment</p>
              {data.devex > 0 && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#0A1738' }}></div>
                  <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                    GB Energy DEVEX: <span className="font-bold">£{data.devex.toFixed(2)}m</span>
                  </span>
                </div>
              )}
              {data.capex > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3A7088' }}></div>
                  <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                    Leveraged CAPEX: <span className="font-bold">£{data.capex.toFixed(2)}m</span>
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Community value section */}
          <div className="space-y-2">
            <p className="text-xs font-semibold mb-2 text-gray-600">Community Value (Cumulative)</p>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#48A46A' }}></div>
              <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                Community Fund: <span className="font-bold">£{data.communityFund.toFixed(2)}m</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#26A69A' }}></div>
              <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                Land Rent: <span className="font-bold">£{data.landRent.toFixed(2)}m</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#81C784' }}></div>
              <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                Business Rates: <span className="font-bold">£{data.businessRates.toFixed(2)}m</span>
              </span>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t-2" style={{ borderColor: '#e0e0e0' }}>
              <span className="text-sm font-bold" style={{ color: '#2C3E50' }}>
                Total Value: <span style={{ color: '#48A46A' }}>£{data.totalCommunity.toFixed(2)}m</span>
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      {/* DEVEX - upper left */}
      <div className="absolute top-2 left-4 bg-white px-4 py-2 rounded-lg shadow-md border-2" style={{ borderColor: '#0A1738', zIndex: 10 }}>
        <p className="text-xs font-medium text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
          DEVEX
        </p>
        <p className="text-2xl font-bold" style={{ color: '#0A1738', fontFamily: 'Montserrat, sans-serif' }}>
          £{(metrics.totalDevex / 1000000).toFixed(1)}m
        </p>
      </div>

      {/* Total Value Generated - upper right */}
      <div className="absolute top-2 right-4 bg-white px-4 py-2 rounded-lg shadow-md border-2" style={{ borderColor: '#48A46A', zIndex: 10 }}>
        <p className="text-xs font-medium text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
          Total Value Generated
        </p>
        <p className="text-2xl font-bold" style={{ color: '#48A46A', fontFamily: 'Montserrat, sans-serif' }}>
          £{year25Total.toFixed(1)}m
        </p>
      </div>

      <ResponsiveContainer width="100%" height={360}>
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            {/* Investment layers - Cold capital in (navy/blue) */}
            <linearGradient id="colorDevex" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0A1738" stopOpacity={0.85}/>
              <stop offset="95%" stopColor="#0A1738" stopOpacity={0.35}/>
            </linearGradient>
            <linearGradient id="colorCapex" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3A7088" stopOpacity={0.75}/>
              <stop offset="95%" stopColor="#3A7088" stopOpacity={0.3}/>
            </linearGradient>
            {/* Return layers - Warm community value out (greens with better differentiation) */}
            <linearGradient id="colorCommunityFund" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#48A46A" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#48A46A" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorLandRent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#26A69A" stopOpacity={0.7}/>
              <stop offset="95%" stopColor="#26A69A" stopOpacity={0.25}/>
            </linearGradient>
            <linearGradient id="colorBusinessRates" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#81C784" stopOpacity={0.65}/>
              <stop offset="95%" stopColor="#81C784" stopOpacity={0.2}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          
          <XAxis 
            dataKey="year" 
            tick={{ fill: '#2C3E50', fontSize: 11, fontFamily: 'Lato, sans-serif' }}
            angle={-45}
            textAnchor="end"
            height={80}
            ticks={['Year 1', 'Year 5', 'Year 10', 'Year 15', 'Year 20', 'Year 25', 'Year 30']}
          />
          
          <YAxis 
            tick={{ fill: '#2C3E50', fontSize: 12, fontFamily: 'Lato, sans-serif' }}
            label={{ 
              value: '£ millions', 
              angle: -90, 
              position: 'insideLeft', 
              fill: '#2C3E50', 
              fontFamily: 'Lato, sans-serif',
              style: { textAnchor: 'middle' }
            }}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          <Legend 
            verticalAlign="top" 
            height={50}
            iconType="square"
            wrapperStyle={{ 
              fontFamily: 'Lato, sans-serif', 
              fontSize: '12px',
              paddingBottom: '10px'
            }}
          />
          
          {/* Investment layers - Cold capital in */}
          {/* GB Energy DEVEX - bottom layer (Years 1-4) */}
          <Area 
            type="monotone" 
            dataKey="devex" 
            stackId="1"
            stroke="#0A1738" 
            strokeWidth={2}
            fill="url(#colorDevex)"
            name="GB Energy DEVEX"
          />
          
          {/* Leveraged CAPEX - second layer (Years 2-6) */}
          <Area 
            type="monotone" 
            dataKey="capex" 
            stackId="1"
            stroke="#3A7088" 
            strokeWidth={2}
            fill="url(#colorCapex)"
            name="Leveraged CAPEX"
          />
          
          {/* Return layers - Warm community value out (starts when turbines operational) */}
          {/* Community Fund */}
          <Area 
            type="monotone" 
            dataKey="communityFund" 
            stackId="1"
            stroke="#48A46A" 
            strokeWidth={1.5}
            fill="url(#colorCommunityFund)"
            name="Community Fund"
          />
          
          {/* Land Rent */}
          <Area 
            type="monotone" 
            dataKey="landRent" 
            stackId="1"
            stroke="#26A69A" 
            strokeWidth={1.5}
            fill="url(#colorLandRent)"
            name="Land Rent"
          />
          
          {/* Business Rates */}
          <Area 
            type="monotone" 
            dataKey="businessRates" 
            stackId="1"
            stroke="#81C784" 
            strokeWidth={1.5}
            fill="url(#colorBusinessRates)"
            name="Business Rates"
          />
          
          {/* Transition marker - when turbines become operational */}
          {transitionYear > 0 && (
            <>
              <line
                x1={`${((transitionYear + 0.5) / chartData.length) * 100}%`}
                y1="0"
                x2={`${((transitionYear + 0.5) / chartData.length) * 100}%`}
                y2="100%"
                stroke="#48A46A"
                strokeWidth={2}
                strokeDasharray="5 5"
                opacity={0.5}
              />
              <text
                x={`${((transitionYear + 0.5) / chartData.length) * 100}%`}
                y="20%"
                textAnchor="middle"
                style={{
                  fontSize: '11px',
                  fontFamily: 'Lato, sans-serif',
                  fill: '#48A46A',
                  fontWeight: '600'
                }}
              >
                ↓ Turbines operational
              </text>
              <text
                x={`${((transitionYear + 0.5) / chartData.length) * 100}%`}
                y="23%"
                textAnchor="middle"
                style={{
                  fontSize: '10px',
                  fontFamily: 'Lato, sans-serif',
                  fill: '#48A46A'
                }}
              >
                Community returns begin
              </text>
            </>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CommunityValueChart;
